import { useState } from 'react';
import { useApolloClient, gql, ApolloCache } from '@apollo/client';
import {
  TenantLeaseInfo,
  SourceType,
  SourceRelationshipIdentifier,
} from 'graphql-query-contracts';
import { fetchTenantLeasesQuery } from 'graphql-queries';
import { FetchTenantLeasesQueryResponse } from './useFetchTenantLeases';

type LeaseItem = {
  id: string;
  leaseId?: string;
  suiteId?: string;
  sourceType?: SourceType;
  source?: SourceRelationshipIdentifier;
};

export const useSaveTenantLeases = () => {
  const [loading, setLoading] = useState(false);
  const apolloClient = useApolloClient();

  const saveLeases = async (
    tenantId: string,
    propertyId: string,
    leaseItemsToRemove: LeaseItem[],
    leaseItemsToAdd: LeaseItem[]
  ) => {
    if (leaseItemsToRemove.length === 0 && leaseItemsToAdd.length === 0) {
      throw new Error('No lease items to save');
    }

    setLoading(true);

    try {
      const { mutationParts, variableDefs, variables } =
        buildMutationPartsAndVariables(
          tenantId,
          propertyId,
          leaseItemsToRemove,
          leaseItemsToAdd
        );
      const mutationString = buildMutationString(variableDefs, mutationParts);
      const dynamicMutation = gql(mutationString);

      const result = await apolloClient.mutate({
        mutation: dynamicMutation,
        variables,
        errorPolicy: 'all',
        update: (cache, { data }) => {
          if (!data) return;

          updateCache(
            cache,
            data,
            leaseItemsToRemove,
            leaseItemsToAdd,
            tenantId,
            propertyId
          );
        },
      });

      setLoading(false);

      return result;
    } catch (err) {
      setLoading(false);

      throw err;
    }
  };

  return {
    loading,
    saveLeases,
  };
};

export const buildMutationPartsAndVariables = (
  tenantId: string,
  propertyId: string,
  leaseItemsToRemove: LeaseItem[],
  leaseItemsToAdd: LeaseItem[]
) => {
  const mutationParts: string[] = [];
  const variableDefs: string[] = ['$tenantId: UUID!', '$propertyId: UUID!'];
  const variables: Record<string, any> = {
    tenantId,
    propertyId,
  };

  leaseItemsToRemove.forEach(item => {
    const alias = item.id;
    const varName = `${alias}RemoveInput`;

    mutationParts.push(
      `${alias}: removeTenantLease(tenantId: $tenantId, propertyId: $propertyId, lease: $${varName}) { leaseId suiteId source { sourceType sourceRelationshipType sourceRelationshipId } }`
    );
    variableDefs.push(`$${varName}: TenantLeaseInfoInput!`);

    // Strip __typename from source if present (GraphQL query results include it)
    const cleanSource = item.source
      ? {
          sourceType: item.source.sourceType,
          sourceRelationshipType: item.source.sourceRelationshipType,
          sourceRelationshipId: item.source.sourceRelationshipId,
        }
      : null;

    variables[varName] = {
      leaseId: item.leaseId || null,
      suiteId: item.suiteId || null,
      source: cleanSource,
    };
  });

  leaseItemsToAdd.forEach(item => {
    const alias = item.id;
    const varName = `${alias}AddInput`;

    mutationParts.push(
      `${alias}: addTenantLease(tenantId: $tenantId, propertyId: $propertyId, lease: $${varName}) { leaseId suiteId suite { id name floors { id index name } } source { sourceType sourceRelationshipType sourceRelationshipId } }`
    );
    variableDefs.push(`$${varName}: TenantLeaseInfoInput!`);

    // Strip __typename from source if present (GraphQL query results include it)
    const cleanSource = item.source
      ? {
          sourceType: item.source.sourceType,
          sourceRelationshipType: item.source.sourceRelationshipType,
          sourceRelationshipId: item.source.sourceRelationshipId,
        }
      : null;

    variables[varName] = {
      leaseId: item.leaseId || null,
      suiteId: item.suiteId || null,
      source: cleanSource,
    };
  });

  return { mutationParts, variableDefs, variables };
};

export const buildMutationString = (
  variableDefs: string[],
  mutationParts: string[]
) => {
  return `mutation SaveTenantLeases(${variableDefs.join(', ')}) {\n  ${mutationParts.join('\n  ')}\n}`;
};

function updateCache(
  cache: ApolloCache<any>,
  data: { [key: string]: TenantLeaseInfo },
  leaseItemsToRemove: LeaseItem[],
  leaseItemsToAdd: LeaseItem[],
  tenantId: string,
  propertyId: string
) {
  const queryParams = {
    query: fetchTenantLeasesQuery,
    variables: { tenantId, propertyId },
  };
  const cacheData =
    cache.readQuery<FetchTenantLeasesQueryResponse>(queryParams);

  if (!cacheData || !cacheData.tenant) return;

  const leases = [...(cacheData.tenant.leases || [])];

  for (const item of leaseItemsToRemove) {
    const alias = item.id;
    const leaseResult = data[alias];

    if (leaseResult) {
      const idx = leases.findIndex(l => isEqualLeaseInfo(leaseResult, l));

      if (idx !== -1) {
        leases.splice(idx, 1);
      }
    }
  }

  for (const item of leaseItemsToAdd) {
    const alias = item.id;
    const leaseResult = data[alias];

    if (leaseResult) {
      const exists = leases.some(l => isEqualLeaseInfo(l, leaseResult));

      if (!exists) {
        leases.push(leaseResult);
      }
    }
  }

  cache.writeQuery({
    ...queryParams,
    data: {
      tenant: {
        ...cacheData.tenant,
        leases,
      },
    },
  });
}

const isEqualLeaseInfo = (a: TenantLeaseInfo, b: TenantLeaseInfo) => {
  const sourceMatch =
    (!a.source && !b.source) ||
    (a.source?.sourceRelationshipId === b.source?.sourceRelationshipId &&
      a.source?.sourceRelationshipType === b.source?.sourceRelationshipType &&
      a.source?.sourceType === b.source?.sourceType);

  return a.leaseId === b.leaseId && a.suiteId === b.suiteId && sourceMatch;
};
